import { Area } from 'ais-facade-api/api';
import { useLazyGetAreasCatalogQuery } from 'app/api/facade';
import { throttle } from 'app/helpers/throttle';
import { Dispatch, SetStateAction, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { t } from 'i18next';
import { SearchStateI } from 'app/hooks/useFilterComboboxState';
import { FormikErrors } from 'formik';
import { FilterFormData } from './FilterPanel';
import { AutoComplete, SelectedOptionI } from '../AutoComplete';
import styles from './FilterPanel.module.scss';

type AreaFieldT = 'inAreaCode' | 'area';

interface AreaInFieldI {
  fieldName: AreaFieldT;
  searchState?: [SearchStateI, Dispatch<SetStateAction<SearchStateI>>];
  onSelect: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<FilterFormData>>;
}

export const AreaInField = ({ fieldName, searchState: searchStateArray, onSelect }: AreaInFieldI) => {
  const [getCatalog, { data: areasData, ...catalogRequest }] = useLazyGetAreasCatalogQuery();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);
  const [areas, setAreas] = useState<Area[] | null>(null);

  const [searchState, setSearchState] = searchStateArray || [];
  const [options, setOptions] = useState<Array<{ title: string; type: string; options: SelectedOptionI[] }> | null>(null);
  const [open, setOpen] = useState(false);

  const initialPlaceholderValue = useMemo(() => searchState?.term['inAreaCode']?.toUpperCase() || '', [searchState]);

  const handleOpen = () => {
    setOpen(true);

    if (catalogRequest.isUninitialized) {
      debouncedGetCatalogHandler({ fetchGeometry: false });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAreaSelect = (selectedOption?: SelectedOptionI | null) => {
    if (!selectedOption?.value) {
      if (fieldName === 'inAreaCode') {
        onSelect?.('inAreaCode', []);
        onSelect?.('selectedInArea', null);
      }
      if (fieldName === 'area') {
        onSelect?.('area', null);
      }
    } else {
      const areasFiltered = areas?.filter((area: Area) => {
        return area.code?.toLowerCase() === selectedOption?.value?.toLowerCase();
      });
      if (areasFiltered) {
        const name = areasFiltered?.[0]?.name;
        if (name) {
          setSearchState?.((prevState: SearchStateI) => ({
            term: {
              ...prevState.term,
              [fieldName]: name,
            },
            visibility: {
              ...prevState.visibility,
              [fieldName]: false,
            },
          }));
        }
        if (fieldName === 'inAreaCode') {
          onSelect?.('inAreaCode', [areasFiltered[0]?.code]);
          onSelect?.('selectedInArea', areasFiltered[0]);
        }
        if (fieldName === 'area') {
          onSelect?.('area', areasFiltered[0]?.code);
        }
      }
      handleClose();
    }
    handleClose();
  };

  useEffect(() => {
    if (areasData && areasData.results && areasData?.results.length > 0) {
      const commonlist = [];
      const userlist = [];

      setAreas(areasData.results);

      const options = areasData.results?.map((area) => ({
        value: area.code,
        label: area.name?.toUpperCase(),
        placeholder: area.name?.toUpperCase(),
        type: area.type,
      }));

      for (let i = 0; i < options?.length; i++) {
        const area = options[i];

        if (area.type === 'COMMON') {
          commonlist.push(area);
        } else if (area.type === 'USER') {
          userlist.push(area);
        }
      }

      setOptions(
        options
          ? [
              { title: t('UserAreasTitle'), type: 'USER', options: userlist },

              { title: t('CommonAreasTitle'), type: 'COMMON', options: commonlist },
            ]
          : null,
      );
    }
  }, [areasData?.results]);

  return (
    <AutoComplete
      className={styles.area_input}
      id={`autocomplete-inAreaCode`}
      placeholder={t('SelectArea')}
      initialValue={initialPlaceholderValue}
      staticSearch={true}
      inputRef={inputRef}
      loading={catalogRequest.isFetching}
      optionsList={options}
      open={open}
      onOpen={handleOpen}
      onSelect={handleAreaSelect}
      onClose={handleClose}
    />
  );
};
